import React from 'react';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutlined';
import { Box, Button, Typography } from '@mui/material';
import { useFieldArray, useFormContext } from 'react-hook-form';
import {
  FINANCIAL_ASSISTANCE_DATE_COLUMN_INDEXES,
  FINANCIAL_ASSISTANCE_TABLE_COLUMN_WIDTHS,
  FINANCIAL_ASSISTANCE_TABLE_MIN_WIDTH,
  getFinancialAssistanceTableHeaders,
} from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/financialCondition/components/financialAssistance/constants.ts';
import { FinancialAssistanceRow } from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/financialCondition/components/financialAssistance/FinancialAssistanceRow.tsx';
import { FinancialAssistanceTotals } from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/financialCondition/components/financialAssistance/FinancialAssistanceTotals.tsx';
import { FinancialConditionTable } from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/financialCondition/components/shared/FinancialConditionTable.tsx';
import type { IFinancialAssistanceGroupProps } from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/financialCondition/components/financialAssistance/types.ts';
import { financialConditionGenerationStyles } from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/financialCondition/styles.ts';
import type {
  TFinancialAssistance,
  TFinancialConditionFormValues,
} from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/financialCondition/types.ts';
import {
  createFinancialAssistance,
  getFinancialAssistanceTotals,
} from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/financialCondition/utils.ts';
import { documentGenerationFormStyles } from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/styles.ts';

export const FinancialAssistanceGroup: React.FC<IFinancialAssistanceGroupProps> = ({
  name,
  title,
  issueOrReceiptDateLabel,
  disabled,
  currencyOptions,
  isCurrenciesLoading,
}) => {
  const { control, watch } = useFormContext<TFinancialConditionFormValues>();
  const { fields, append, remove } = useFieldArray({ control, name });
  const assistance = watch(name) as TFinancialAssistance[];
  const totals = getFinancialAssistanceTotals(assistance ?? []);

  return (
    <Box sx={financialConditionGenerationStyles.financialAssistanceGroup}>
      <Typography sx={{ fontWeight: 700 }}>{title}</Typography>

      <FinancialConditionTable
        ariaLabel={title}
        headers={getFinancialAssistanceTableHeaders(issueOrReceiptDateLabel)}
        showActions={fields.length > 1}
        dateColumnIndexes={[...FINANCIAL_ASSISTANCE_DATE_COLUMN_INDEXES]}
        columnWidths={[...FINANCIAL_ASSISTANCE_TABLE_COLUMN_WIDTHS]}
        minWidth={FINANCIAL_ASSISTANCE_TABLE_MIN_WIDTH}
      >
        {fields.map((field, index) => (
          <FinancialAssistanceRow
            key={field.id}
            name={name}
            index={index}
            canRemove={fields.length > 1}
            issueOrReceiptDateLabel={issueOrReceiptDateLabel}
            disabled={disabled}
            currencyOptions={currencyOptions}
            isCurrenciesLoading={isCurrenciesLoading}
            onRemove={() => remove(index)}
          />
        ))}
      </FinancialConditionTable>

      <Button
        variant="outlined"
        startIcon={<AddCircleOutlineIcon />}
        onClick={() => append(createFinancialAssistance())}
        disabled={disabled}
        sx={documentGenerationFormStyles.addButton}
      >
        Додати допомогу
      </Button>

      <FinancialAssistanceTotals totals={totals} ariaLabel={`Підсумки: ${title.toLowerCase()}`} />
    </Box>
  );
};
